import React, { useEffect, useState } from 'react'
import moment from 'moment'
import ReactPaginate from 'react-paginate'
import toast from 'react-hot-toast'
import { useSearchParams } from 'react-router-dom'
import Spinwheel from '../../components/common/Spinwheel'
import { useUserContext } from '../../context/userContext'
import { getRAReportsApi } from '../../utils/ApiEndpointFunctions'

const PAGE_LIMIT = 10

const toNumber = value => {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

const formatNumber = value => toNumber(value).toLocaleString('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const formatMeasurement = value => toNumber(value) === 0 ? '' : formatNumber(value)

const getOrdinal = number => {
  const value = Number(number) || 0
  const remainder = value % 100
  if (remainder >= 11 && remainder <= 13) return `${value}th`
  if (value % 10 === 1) return `${value}st`
  if (value % 10 === 2) return `${value}nd`
  if (value % 10 === 3) return `${value}rd`
  return `${value}th`
}

const getRowDescription = row => row?.subPart || row?.workDescription || row?.itemName || ''

function RAReports() {
  const { langPreference } = useUserContext()
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [reports, setReports] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [reportDetails, setReportDetails] = useState(null)

  const billType = searchParams.get('tab') || 'client'
  const reportId = searchParams.get('reportId') || ''
  const page = Number(searchParams.get('reportPage')) || 1

  const loadReports = async () => {
    setLoading(true)
    try {
      const res = await getRAReportsApi('', billType, PAGE_LIMIT, page)
      setReports(res?.data?.data?.reports || [])
      setTotalCount(res?.data?.data?.totalCount || 0)
    } catch (error) {
      setReports([])
      toast.error(error?.response?.data?.message || 'Failed to load RA reports.')
    }
    setLoading(false)
  }

  const loadReportDetails = async id => {
    setLoading(true)
    try {
      const res = await getRAReportsApi(id, billType)
      setReportDetails(res?.data?.data || null)
    } catch (error) {
      setReportDetails(null)
      toast.error(error?.response?.data?.message || 'Failed to load RA report.')
    }
    setLoading(false)
  }

  useEffect(() => {
    if (reportId) {
      loadReportDetails(reportId)
    } else {
      setReportDetails(null)
      loadReports()
    }
  }, [reportId, billType, page])

  const updateParams = updates => {
    const params = new URLSearchParams(searchParams)
    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined) params.delete(key)
      else params.set(key, value)
    })
    setSearchParams(params)
  }

  const renderMeasurementRows = item => {
    let previousCommodityPart = ''
    let previousCommoditySubPart = ''
    let previousWorkDescription = ''

    return item?.measurementRows?.map((row, index) => {
      const headings = []
      if (row?.commodityPart && row.commodityPart !== previousCommodityPart) headings.push(row.commodityPart)
      if (row?.commoditySubPart && row.commoditySubPart !== previousCommoditySubPart) headings.push(row.commoditySubPart)
      if (row?.workDescription && row.workDescription !== previousWorkDescription && row?.subPart) headings.push(row.workDescription)

      previousCommodityPart = row?.commodityPart || previousCommodityPart
      previousCommoditySubPart = row?.commoditySubPart || previousCommoditySubPart
      previousWorkDescription = row?.workDescription || previousWorkDescription

      return (
        <React.Fragment key={row?.dprItemQtyId || index}>
          {headings.map((heading, headingIndex) => (
            <tr className="ra-report-section-row" key={`${row?.dprItemQtyId}-${headingIndex}`}>
              <td></td>
              <td colSpan={9}>{heading}</td>
            </tr>
          ))}
          <tr>
            <td></td>
            <td>{getRowDescription(row)}</td>
            <td className="text-end">{formatMeasurement(row?.number1)}</td>
            <td className="text-end">{formatMeasurement(row?.number2)}</td>
            <td className="text-end">{formatMeasurement(row?.length)}</td>
            <td className="text-end">{formatMeasurement(row?.breadth)}</td>
            <td className="text-end">{formatMeasurement(row?.height)}</td>
            <td className="text-end">{formatMeasurement(row?.diameter)}</td>
            <td className="text-end">{formatNumber(row?.quantity)}</td>
            <td className="text-center">{item?.unitType}</td>
          </tr>
        </React.Fragment>
      )
    })
  }

  const renderSummaryRow = (label, quantity, unitType) => (
    <tr className="ra-report-summary-row" key={label}>
      <td></td>
      <td colSpan={7} className="text-end">{label}</td>
      <td className="text-end">{formatNumber(quantity)}</td>
      <td className="text-center">{unitType}</td>
    </tr>
  )

  if (loading) {
    return <div className="d-flex justify-content-center py-5"><Spinwheel /></div>
  }

  if (reportId && reportDetails?.report) {
    const { report, items = [] } = reportDetails
    const categoryGroups = items.reduce((groups, item) => {
      const category = item?.category || 'Uncategorised'
      if (!groups[category]) groups[category] = []
      groups[category].push(item)
      return groups
    }, {})

    return (
      <div className="main-content ra-report-view">
        <div className="container-fluid">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <button type="button" className="btn btn-outline-dark" onClick={() => updateParams({ reportId: '' })}>
              <i className="bi bi-chevron-left"></i> {langPreference?.back || 'Back'}
            </button>
            <div className="text-end">
              <h3 className="mb-0 font-blue">{getOrdinal(report?.reportSequence)} {langPreference?.ra_report || 'RA Report'}</h3>
              <div>{report?.raBillNo} | {moment(report?.billDate).format('DD/MM/YYYY')}</div>
            </div>
          </div>

          {Object.entries(categoryGroups).map(([category, categoryItems]) => (
            <div className="table-responsive mb-4" key={category}>
              <table className="table table-bordered ra-report-table">
                <thead>
                  <tr><th colSpan={10} className="ra-report-title">{category.toUpperCase()}</th></tr>
                  <tr>
                    <th>S.<br />NO.</th>
                    <th>{langPreference?.item_measurement_details || 'Item / Measurement Details'}</th>
                    <th>{langPreference?.no_ || 'No.'} 1</th>
                    <th>{langPreference?.no_ || 'No.'} 2</th>
                    <th>{langPreference?.length || 'L'}</th>
                    <th>{langPreference?.breadth || 'B'}</th>
                    <th>{langPreference?.height || 'H'}</th>
                    <th>{langPreference?.diameter || 'D'}</th>
                    <th>{langPreference?.qty || 'Qty'}</th>
                    <th>{langPreference?.unit || 'Unit'}</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryItems.map(item => (
                    <React.Fragment key={item?._id}>
                      <tr className="ra-report-item-row">
                        <td className="text-center">{item?.reportSNo}</td>
                        <td>{item?.itemName}</td>
                        <td colSpan={8}></td>
                      </tr>
                      <tr>
                        <td></td>
                        <td colSpan={7}>{langPreference?.last_paid_qty || 'Last Paid Qty.'}{item?.boqNumber ? ` | BOQ No. ${item.boqNumber}` : ''}</td>
                        <td className="text-end">{formatNumber(item?.lastPaidQty)}</td>
                        <td className="text-center">{item?.unitType}</td>
                      </tr>
                      {renderMeasurementRows(item)}
                      {renderSummaryRow(langPreference?.upto_date_qty || 'Upto Date Qty.', item?.uptoDateQty, item?.unitType)}
                      {renderSummaryRow(langPreference?.last_paid_qty || 'Last Paid Qty.', item?.lastPaidQty, item?.unitType)}
                      {renderSummaryRow(langPreference?.now_pay_qty || 'Now Pay Qty.', item?.nowPayQty, item?.unitType)}
                      <tr className="ra-report-spacer-row"><td colSpan={10}></td></tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="main-content">
      <div className="container-fluid">
        <div className="dash-nav1">
          <h3 className="mb-0 font-blue">{langPreference?.ra_reports || 'RA Reports'}</h3>
        </div>
        <div className="table-responsive">
          <table className="table d-table mt-3">
            <thead>
              <tr>
                <th>{langPreference?.report || 'Report'}</th>
                <th>{langPreference?.ra_bill_no || 'RA Bill No'}</th>
                <th>{langPreference?.bill_date || 'Bill Date'}</th>
                <th>{langPreference?.categories || 'Categories'}</th>
                <th>{langPreference?.generated_on || 'Generated On'}</th>
                <th>{langPreference?.action || 'Action'}</th>
              </tr>
            </thead>
            <tbody>
              {reports.length ? reports.map(report => (
                <tr key={report?._id}>
                  <td>{getOrdinal(report?.reportSequence)} RA</td>
                  <td>{report?.raBillNo}</td>
                  <td>{moment(report?.billDate).format('DD/MM/YYYY')}</td>
                  <td>{report?.categories?.join(', ') || '-'}</td>
                  <td>{moment(report?.generatedOn).format('DD/MM/YYYY hh:mm A')}</td>
                  <td><button type="button" className="btn btn-outline-primary" onClick={() => updateParams({ reportId: report?._id })}>{langPreference?.view || 'View'}</button></td>
                </tr>
              )) : (
                <tr><td colSpan={6} className="text-center py-4">{langPreference?.data_not_found || 'Data Not Found'}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {totalCount > PAGE_LIMIT && (
          <ReactPaginate
            nextLabel={`${langPreference?.next_page || 'Next'} >`}
            previousLabel={`< ${langPreference?.previous_page || 'Previous'}`}
            onPageChange={event => updateParams({ reportPage: event.selected + 1 })}
            pageCount={Math.ceil(totalCount / PAGE_LIMIT)}
            forcePage={page - 1}
            pageRangeDisplayed={3}
            marginPagesDisplayed={1}
            containerClassName="pagination"
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            activeClassName="active"
          />
        )}
      </div>
    </div>
  )
}

export default RAReports
